import { uuid as UUID } from "vue-uuid";
import { KtznSubscribeEvent } from "@/framework/network/websocket/ktznWebsocketService";
import { err, ok, Result } from "neverthrow";
import KtznEventSubscriptionError from "@/framework/errors/websocket/ktznEventSubscriptionError";

/**
 * Represents the current state of a subscription.
 */
export enum KtznEventSubscriptionState {
  Initial = "INITIAL",
  Connecting = "CONNECTING",
  Connected = "CONNECTED",
  Disconnected = "DISCONNECTED",
  Failure = "FAILURE",
}

/**
 * Represents a Kotozna event subscription.
 * Holds the subscribe event with an ID, state and optional error.
 */
export type KtznEventSubscription = {
  /**
   * Unique identifier
   */
  id: string;
  /**
   * Current state of the subscription
   */
  state: KtznEventSubscriptionState;
  /**
   * Represents a ktzn subscribe websocket event that is used to communicate with the backend.
   */
  event: KtznSubscribeEvent;
  /**
   * Subscription Error
   */
  error?: Error;
};

/**
 * Interface for a simple, memory based cache to manage the Kotozna subscriptions.
 */
export interface IKtznWebsocketSubscriptionCache {
  /**
   * Lists KtznEventSubscription items
   * @returns A list of KtznEventSubscription items
   */
  list(): Set<KtznEventSubscription>;

  /**
   * Lists the subscribed KtznEventSubscription items. Where KtznEventSubscriptionState == CONNECTED.
   * @returns A list of currently subscribed KtznEventSubscription items
   */
  listSubscribed(): Set<KtznEventSubscription>;

  /**
   * Lists the unsubscribed KtznEventSubscription items. Where KtznEventSubscriptionState != CONNECTED.
   * @returns A list of currently sunsubscribed KtznEventSubscription items
   */
  listUnsubscribed(): Set<KtznEventSubscription>;

  /**
   * Gets a subscription by name
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @returns KtznEventSubscription or null
   */
  getByName(subscribeName: string): Result<KtznEventSubscription, Error>;

  /**
   * Returns if a subscription exists in the cache
   * @param subscription
   * @returns if a subscription exists in the cache
   */
  exist(subscription: KtznEventSubscription): boolean;

  /**
   * Returns is a subscription exists in the cache
   * @param event KtznSubscribeEvent
   * @returns if a subscription exists in the cache
   */
  existByEvent(event: KtznSubscribeEvent): boolean;

  /**
   * Returns if a subscription exists in the cache
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @returns if a subscription exists in the cache
   */
  existByName(subscribeName: string): boolean;

  /**
   * Returns a subscription state
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   */
  stateByName(subscribeName: string): KtznEventSubscriptionState | null;

  /**
   * Adds a KtznEventSubscription to the cache
   * @param subscription KtznEventSubscription to be added to the cache
   * @returns The added KtznEventSubscription
   */
  add(subscription: KtznEventSubscription): Result<KtznEventSubscription, KtznEventSubscriptionError>;

  /**
   * Adds a KtznEventSubscription to the cache by event
   * @param event: KtznSubscribeEvent
   * @returns The added KtznEventSubscription
   */
  addByEvent(event: KtznSubscribeEvent): Result<KtznEventSubscription, KtznEventSubscriptionError>;

  /**
   * Updates the KtznEventSubscriptionState for a KtznEventSubscription.
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @param state: KtznEventSubscriptionState
   * @returns KtznEventSubscription | null
   */
  updateState(
    subscribeName: string,
    state: KtznEventSubscriptionState
  ): Result<KtznEventSubscription, KtznEventSubscriptionError>;

  /**
   * Sets all subscription states to disconnected.
   */
  unsubscribeAll(): void;

  /**
   * Removes a KtznEventSubscription from the cache
   * @param subscription KtznEventSubscription to be removed from the cache
   * @returns null
   */
  remove(subscription: KtznEventSubscription): Result<null, KtznEventSubscriptionError>;

  /**
   * Removes a KtznEventSubscription via the KtznSubscribeEvent from the cache
   * @param event KtznSubscribeEvent for KtznEventSubscription that should be removed from the cache
   * @returns null
   */
  removeByEvent(event: KtznSubscribeEvent): Result<null, KtznEventSubscriptionError>;

  /**
   * Removes all KtznEventSubscriptions from the cache.
   */
  removeAll(): void;
}

/**
 * A simple, memory based cache to manage the Kotozna subscriptions.
 */
export class KtznWebsocketSubscriptionCache implements IKtznWebsocketSubscriptionCache {
  /**
   * KtznEventSubscription set
   */
  private _subscriptions = new Set<KtznEventSubscription>();

  /**
   * Lists KtznEventSubscription items
   * @returns A list of KtznEventSubscription items
   */
  list = (): Set<KtznEventSubscription> => {
    return this._subscriptions;
  };

  /**
   * Lists the subscribed KtznEventSubscription items. Where KtznEventSubscriptionState == CONNECTED.
   * @returns A list of currently subscribed KtznEventSubscription items
   */
  listSubscribed = (): Set<KtznEventSubscription> => {
    const newSet = new Set<KtznEventSubscription>();
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (item.state == KtznEventSubscriptionState.Connected) {
        newSet.add(item);
      }
    });
    this._subscriptions = newSet;
    return newSet;
  };

  /**
   * Lists the unsubscribed KtznEventSubscription items. Where KtznEventSubscriptionState != CONNECTED.
   * @returns A list of currently sunsubscribed KtznEventSubscription items
   */
  listUnsubscribed = (): Set<KtznEventSubscription> => {
    const newSet = new Set<KtznEventSubscription>();
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (item.state != KtznEventSubscriptionState.Connected) {
        newSet.add(item);
      }
    });
    this._subscriptions = newSet;
    return newSet;
  };

  /**
   * Gets a subscription by name
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @returns KtznEventSubscription or null
   */
  getByName = (subscribeName: string): Result<KtznEventSubscription, KtznEventSubscriptionError> => {
    let subscription: KtznEventSubscription | null = null;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (subscribeName === item.event.subscribe_name) {
        subscription = item;
      }
    });
    if (subscription) {
      return ok(subscription);
    } else {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.cacheSubscriptionDontExists,
          `Subscription with name: ${subscribeName} does not exist in cache.`
        )
      );
    }
  };

  /**
   * Returns if a subscription exists in the cache
   * @param subscription
   * @returns if a subscription exists in the cache
   */
  exist = (subscription: KtznEventSubscription): boolean => {
    let found = false;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (subscription.id === item.id) {
        found = true;
      }
    });
    return found;
  };

  /**
   * Returns is a subscription exists in the cache
   * @param event KtznSubscribeEvent
   * @returns if a subscription exists in the cache
   */
  existByEvent = (event: KtznSubscribeEvent): boolean => {
    let found = false;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (event.subscribe_name === item.event.subscribe_name) {
        found = true;
      }
    });
    return found;
  };

  /**
   * Returns if a subscription exists in the cache
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @returns if a subscription exists in the cache
   */
  existByName = (subscribeName: string): boolean => {
    let found = false;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (subscribeName === item.event.subscribe_name) {
        found = true;
      }
    });
    return found;
  };

  /**
   * Returns the current subscription state give a `subscribeName`.
   * @param subscribeName Subscription name (e.g. `tenant/1`)
   * @returns The subscription state or null if no state is found.
   */
  stateByName = (subscribeName: string): KtznEventSubscriptionState | null => {
    let subscriptionState: KtznEventSubscriptionState | null = null;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (subscribeName === item.event.subscribe_name) {
        subscriptionState = item.state;
      }
    });
    return subscriptionState;
  };

  /**
   * Adds a KtznEventSubscription to the cache
   * @param subscription KtznEventSubscription to be added to the cache
   * @returns The added KtznEventSubscription
   */
  add = (subscription: KtznEventSubscription): Result<KtznEventSubscription, KtznEventSubscriptionError> => {
    let found = false;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (subscription.id === item.id) {
        found = true;
      }
    });

    if (!found) {
      this._subscriptions.add(subscription);
      return ok(subscription);
    } else {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.cacheSubscriptionExists,
          `Subscription with id: ${subscription.id} does already exist in cache.`
        )
      );
    }
  };

  /**
   * Adds a KtznEventSubscription to the cache by event
   * @param event: KtznSubscribeEvent
   * @returns The added KtznEventSubscription
   */
  addByEvent = (event: KtznSubscribeEvent): Result<KtznEventSubscription, KtznEventSubscriptionError> => {
    let found = false;
    event.method = "ktzn_auth";
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (event.subscribe_name === item.event.subscribe_name) {
        found = true;
      }
    });

    if (!found) {
      const newSubscription: KtznEventSubscription = {
        id: UUID.v4(),
        state: KtznEventSubscriptionState.Connecting,
        event: event,
      };
      this._subscriptions.add(newSubscription);
      return ok(newSubscription);
    } else {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.cacheSubscriptionExists,
          `Subscription with event: ${event.subscribe_name} does already exist in cache.`
        )
      );
    }
  };

  /**
   * Updates the KtznEventSubscriptionState for a KtznEventSubscription.
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @param state: KtznEventSubscriptionState
   * @returns KtznEventSubscription | null
   */
  updateState = (
    subscribeName: string,
    state: KtznEventSubscriptionState
  ): Result<KtznEventSubscription, KtznEventSubscriptionError> => {
    let subscription: KtznEventSubscription | undefined;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (subscribeName === item.event.subscribe_name) {
        item.state = KtznEventSubscriptionState.Connected;
        subscription = item;
      }
    });
    if (subscription) {
      return ok(subscription);
    } else {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.cacheSubscriptionDontExists,
          `Subscription with name: ${subscribeName} does not exist in cache.`
        )
      );
    }
  };

  /**
   * Sets all subscription states to disconnected.
   */
  unsubscribeAll = (): void => {
    const newSet = new Set<KtznEventSubscription>();
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      const newItem: KtznEventSubscription = {
        id: item.id,
        state: KtznEventSubscriptionState.Disconnected,
        event: item.event,
        error: item.error,
      };
      newSet.add(newItem);
    });
    this._subscriptions = newSet;
  };

  /**
   * Removes a KtznEventSubscription from the cache
   * @param subscription KtznEventSubscription to be removed from the cache
   * @returns null
   */
  remove = (subscription: KtznEventSubscription): Result<null, KtznEventSubscriptionError> => {
    let foundSubscription: KtznEventSubscription | undefined;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (subscription.id === item.id) {
        foundSubscription = item;
      }
    });
    if (foundSubscription) {
      this._subscriptions.delete(foundSubscription);
      return ok(null);
    } else {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.cacheSubscriptionDontExists,
          `Subscription for event: ${event} not found in cache`
        )
      );
    }
  };

  /**
   * Removes a KtznEventSubscription via the KtznSubscribeEvent from the cache
   * @param event KtznSubscribeEvent for KtznEventSubscription that should be removed from the cache
   * @returns null
   */
  removeByEvent = (event: KtznSubscribeEvent): Result<null, KtznEventSubscriptionError> => {
    let subscription: KtznEventSubscription | undefined;
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      if (event.subscribe_name === item.event.subscribe_name) {
        subscription = item;
      }
    });
    if (subscription) {
      this._subscriptions.delete(subscription);
      return ok(null);
    } else {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.cacheSubscriptionDontExists,
          `Subscription for event: ${event} not found in cache`
        )
      );
    }
  };

  /**
   * Removes all KtznEventSubscriptions from the cache.
   */
  removeAll = (): void => {
    this._subscriptions.forEach((item: KtznEventSubscription) => {
      this.remove(item);
    });
  };
}
